import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Button from '../Button/Button';
import translations from '../../translations';
import GlobalConfig from '../../shared/GlobalConfig';
import agreementActions from '../../store/actions/agreementAction';
import configActions from '../../store/actions/configAction';
import VVEGuestConsent from '../AbridgeConsent/VVEGuestConsent';

const ALLOWED_DESKTOP_OS = [GlobalConfig.IOS, GlobalConfig.AOS];
const ALLOWED_VISIT_TYPES = [GlobalConfig.OP_VISIT_TYPE, GlobalConfig.NI_VISIT_TYPE];

const AbridgeConsent = abridgeConsentProps => {
  // parse props
  const {
    visitType,
    validateConfig: { ambntlistngConsent = {}, ...validateConfigData },
    deviceDetails: { deviceType, deviceOs }
  } = abridgeConsentProps;
  const { launchType } = useSelector(state => state?.appConfig);
  const dispatch = useDispatch();

  // parse dependent store values
  const { lang: localizedLanguageCode } = useSelector(state => state?.localization);

  const translationRef = useRef(translations[localizedLanguageCode]);

  // set local state variables
  const [consentVerbiageList, setConsentVerbiageList] = useState(null);

  const updateStoreBannerDetails = (displayBannerStatus = null) => {
    if (launchType === GlobalConfig.LAUNCH_VVEGUEST) return;
    const defaultPayloadParams = { alertType: 'warning', hideAlertIcon: null };
    const actionPayload = { ...defaultPayloadParams, displayBanner: displayBannerStatus, ...translationRef.current.ABRIDGE_TIMEOUT_BANNER };
    dispatch(configActions.setBannerOptions(actionPayload));
  };

  const submitUserConsent = ambntlistngConsentStatus => {
    const abridgeConsentActionPayload = {
      validateConfigData,
      ambntlistngConsentStatus,
      vveConsentStatus: launchType === GlobalConfig.LAUNCH_VVEGUEST ? translationRef.current.NA : translationRef.current.ACCEPT
    };
    dispatch(agreementActions.submitAbridgeConsentAction(abridgeConsentActionPayload));
  };

  const startNoConsentSubmissionTimer = () => {
    const { noConsentBannerDisplayTimeout = null } = ambntlistngConsent;
    if (noConsentBannerDisplayTimeout && launchType !== GlobalConfig.LAUNCH_VVEGUEST) {
      setTimeout(() => {
        submitUserConsent(GlobalConfig.AMBNT_CONSENT_NOCONSENT);
        updateStoreBannerDetails(false);
      }, noConsentBannerDisplayTimeout);
    }
  };

  const startAbridgeIdleTimer = () => {
    const { idleTimeout = null } = ambntlistngConsent;
    if (idleTimeout) {
      if (launchType === GlobalConfig.LAUNCH_VVEGUEST) {
        setTimeout(() => {
          submitUserConsent(GlobalConfig.AMBNT_CONSENT_NOCONSENT);
        }, idleTimeout);
      } else {
        setTimeout(() => {
          startNoConsentSubmissionTimer();
          updateStoreBannerDetails(true);
        }, idleTimeout);
      }
    }
  };

  // action handlers
  const handleAccept = () => {
    submitUserConsent(GlobalConfig.AMBNT_CONSENT_AGREE);
  };

  const handleDecline = () => {
    submitUserConsent(GlobalConfig.AMBNT_CONSENT_DISAGREE);
  };

  // layout elements
  let acceptButtonText;
  if (ALLOWED_VISIT_TYPES.includes(visitType)) {
    acceptButtonText = translationRef.current.ACCEPT_BTN;
  } else {
    acceptButtonText = translationRef.current.ACCEPT_BUTTON;
  }
  const agreeButtonText = launchType === GlobalConfig.LAUNCH_VVEGUEST ? translationRef.current.OK : acceptButtonText;

  const AgreeButtonElement = (
    <Button modal="" disabled={false} tabIndex="0" refValue="" analyticsName="" onClick={handleAccept} class="vve-modal-agree" id="vve-modal-agree">
      {agreeButtonText}
    </Button>
  );

  const DeclineButtonElement = (
    <Button
      modal=""
      disabled={false}
      tabIndex="0"
      refValue=""
      analyticsName=""
      onClick={handleDecline}
      class="vve-modal-decline"
      id="vve-modal-decline"
    >
      {translationRef.current.DECLINE_BUTTON}
    </Button>
  );

  const renderAmbientActionButtons = () => {
    let actionButtonList = [DeclineButtonElement, AgreeButtonElement];
    const isMobileDevice = deviceType === GlobalConfig.MOBILE;
    if (launchType === GlobalConfig.LAUNCH_VVEGUEST) {
      return <>{AgreeButtonElement}</>;
    }
    if (isMobileDevice || ALLOWED_DESKTOP_OS.includes(deviceOs)) {
      actionButtonList = actionButtonList.reverse();
    }
    return actionButtonList.map(buttonElement => <>{buttonElement}</>);
  };

  // parse and set all depndencies based on localization
  useEffect(() => {
    document.documentElement.lang = localizedLanguageCode;
    const parsedTranslation = translations[localizedLanguageCode];
    translationRef.current = parsedTranslation;
    updateStoreBannerDetails();
    const consentLanguageKey = localizedLanguageCode === 'en' ? 'english' : 'spanish';
    setConsentVerbiageList(ambntlistngConsent[consentLanguageKey] ?? null);
  }, [localizedLanguageCode]);

  useEffect(() => {
    startAbridgeIdleTimer();
  }, []);

  return (
    <>
      {consentVerbiageList && (
        <>
          <ul className="Agreement-list vve-agreement-noList">
            {launchType === GlobalConfig.LAUNCH_VVEGUEST ? (
              <VVEGuestConsent consentVerbiageList={consentVerbiageList} />
            ) : (
              consentVerbiageList.map((content, index) => <li key={`abridge-${index}`}>{content}</li>)
            )}
          </ul>
          <div className="vve-agreement-button-div">{renderAmbientActionButtons()}</div>
        </>
      )}
    </>
  );
};

AbridgeConsent.propTypes = {
  visitType: PropTypes.string,
  validateConfig: PropTypes.shape({
    ambntlistngConsent: PropTypes.object
  }),
  deviceDetails: PropTypes.shape({
    deviceType: PropTypes.string,
    deviceOs: PropTypes.string
  })
};

export default AbridgeConsent;
